import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Paper,
  Divider,
  Link,
  TextField,
  Chip,
  CardMedia,
  Alert
} from '@mui/material';
import Modal from 'react-modal';
import { toast } from 'react-toastify';

/**
 * ส่งตรวจ (Mercari Anshin appraisal) — reading the result off Mercari.
 *
 * The parcel reached the inspection centre some days ago and the centre posts its
 * verdict on the Mercari transaction page. Nothing here fetches that; an admin
 * opens the page, looks, and records what it says. So the whole modal is built
 * around one link and three answers:
 *
 *   ผ่าน        the item is genuine — the normal flow takes over
 *   ไม่ผ่าน      Mercari cancels and the item goes back to the seller
 *   ยังไม่เสร็จ   no verdict yet — come back tomorrow
 *
 * "ยังไม่เสร็จ" is the common answer, not the exceptional one, which is why it is
 * a plain button and not buried: an appraisal can easily take several checks.
 *
 * ไม่ผ่าน ENDS THE ORDER. It goes into the cancel funnel with its reason set,
 * and a customer with a wallet is refunded on the spot. That is real money
 * leaving on one click, so the confirmation states the actual outcome — fetched
 * from the server, not guessed here — and names the exact figure when one is
 * going to move. An admin who misreads this either strands a customer's money
 * or pays them twice, so the sentence has to be specific.
 */

// The sentence an admin reads before ending an order. Each case says what will
// actually happen — never a generic "are you sure", because the three outcomes
// differ in whether money moves at all.
const FailureWarning = ({ outcome }) => {
  if (!outcome) {
    return (
      <Alert severity="info" icon={<CircularProgress size={18} />} sx={{ mb: 2 }}>
        กำลังตรวจสอบยอดที่ต้องคืน...
      </Alert>
    );
  }

  // The preview failed. Say only what is certain (the order ends) and send the
  // admin to check the money themselves, rather than promise either way.
  if (outcome.unavailable) {
    return (
      <Alert severity="warning" sx={{ mb: 2 }}>
        ยืนยัน "ไม่ผ่าน" — ระบบจะยกเลิกออเดอร์นี้ (ตรวจสอบยอดคืนไม่สำเร็จ
        กรุณาเช็ครายการยกเลิกอีกครั้งหลังบันทึก)
      </Alert>
    );
  }

  if (outcome.willRefundToWallet) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        ยืนยัน "ไม่ผ่าน" — ระบบจะยกเลิกออเดอร์นี้ และ<b>คืนเงิน ฿
        {Number(outcome.amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}{' '}
        เข้ากระเป๋าเงินลูกค้าทันที</b> พร้อมแจ้งลูกค้าให้อัตโนมัติ
      </Alert>
    );
  }

  // Nothing to give back: unpaid, or the order already ended.
  if (outcome.reason === 'nothing-to-refund') {
    return (
      <Alert severity="info" sx={{ mb: 2 }}>
        ยืนยัน "ไม่ผ่าน" — ออเดอร์นี้ยังไม่ได้ชำระเงินหรือถูกยกเลิกไปแล้ว จึงไม่มีการคืนเงิน
      </Alert>
    );
  }

  // A human finishes it — whether because the customer has no wallet or because
  // no charge is on file to credit. The two are one message on purpose: the next
  // action is the same either way, and the cancel list already carries the
  // refund from here, with its own reminders.
  return (
    <Alert severity="warning" sx={{ mb: 2 }}>
      ยืนยัน "ไม่ผ่าน" — ระบบจะยกเลิกออเดอร์นี้และส่งเข้ารายการยกเลิก (เหตุผล: ตรวจไม่ผ่าน)
    </Alert>
  );
};
const AppraisalModal = ({ isOpen, onClose, item, onRecordResult, onDefer, onPreviewFailure, style }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [note, setNote] = useState('');
  const [confirmingFail, setConfirmingFail] = useState(false);
  // null while it is being fetched, so the confirmation never claims an outcome
  // it has not been told.
  const [failureOutcome, setFailureOutcome] = useState(null);

  const reset = () => {
    setNote('');
    setConfirmingFail(false);
    setFailureOutcome(null);
  };

  // Asked when the admin reaches for ไม่ผ่าน, not when the modal opens: it is
  // only needed on that path, and it must reflect the order as it is now.
  const askWhatFailureWouldDo = async () => {
    setConfirmingFail(true);
    setFailureOutcome(null);
    try {
      const res = await onPreviewFailure?.(item.orderId);
      setFailureOutcome(res || { unavailable: true });
    } catch (error) {
      // The preview is a courtesy; losing it must not block the verdict. But it
      // must not vanish silently either — the fallback wording admits only that
      // the order ends, and an admin seeing it needs to know why it appeared.
      console.error('[appraisal] failure preview failed:', error?.response?.status, error?.message);
      setFailureOutcome({ unavailable: true });
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const submit = async (action) => {
    setIsSubmitting(true);
    try {
      await action();
      reset();
    } catch (error) {
      toast.error(
        error?.response?.data?.message || 'เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePassed = () =>
    submit(() => onRecordResult({ orderId: item.orderId, result: 'passed', note }));

  const handleFailed = () =>
    submit(() => onRecordResult({ orderId: item.orderId, result: 'failed', note }));

  const handleDefer = () => submit(() => onDefer(item.orderId));

  // How long this has sat at the centre — the only number that tells an admin
  // whether to expect a verdict yet.
  const daysSinceArrival = item?.appraisalArrivedAt
    ? Math.floor((Date.now() - new Date(item.appraisalArrivedAt).getTime()) / 86400000)
    : null;

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={handleClose}
      style={style}
      ariaHideApp={false}
      shouldCloseOnOverlayClick={false}
    >
      <Paper>
        <Box p={3}>
          {!item ? (
            <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
              <CircularProgress />
              <Typography variant="h6" color="text.secondary">
                กำลังโหลดข้อมูล...
              </Typography>
            </Box>
          ) : (
            <>
              <Box display="flex" alignItems="center" gap={2} mb={1}>
                <Chip
                  label="🔍 ส่งตรวจ (Anshin)"
                  sx={{
                    fontSize: '1.1rem',
                    height: '32px',
                    backgroundColor: '#fff8e1',
                    color: '#8d6e00',
                    border: '1px solid #ffe082',
                    '& .MuiChip-label': { px: 2 }
                  }}
                />
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#1a1a1a' }}>
                  ผลการตรวจสอบสินค้า
                </Typography>
              </Box>

              {daysSinceArrival !== null && (
                <Typography variant="body2" color="text.secondary">
                  ถึงศูนย์ตรวจสอบแล้ว {daysSinceArrival} วัน
                </Typography>
              )}

              <Divider sx={{ my: 2 }} />

              <Box display="flex" gap={2} mb={3}>
                {item.imageUrl && (
                  <CardMedia
                    component="img"
                    image={item.imageUrl}
                    alt={item.title || 'item'}
                    sx={{ width: 120, height: 120, objectFit: 'cover', borderRadius: 1 }}
                  />
                )}
                <Box flex={1}>
                  {item.title && (
                    <Typography variant="body1" sx={{ fontWeight: 500 }} gutterBottom>
                      {item.title}
                    </Typography>
                  )}
                  <Typography variant="body2" color="text.secondary">
                    ลูกค้า: {item.smileName || 'N/A'}
                  </Typography>
                  {item.appraisalTrackingJp && (
                    <Typography variant="body2" color="text.secondary">
                      เลขพัสดุส่งตรวจ: {item.appraisalTrackingJp}
                    </Typography>
                  )}
                </Box>
              </Box>

              <Box mb={3}>
                <Typography variant="subtitle1" fontWeight="bold" color="primary" gutterBottom>
                  เปิดหน้า Mercari เพื่อดูผลตรวจ
                </Typography>
                {item.itemUrl ? (
                  <Link
                    href={item.itemUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      wordBreak: 'break-all',
                      color: 'primary.main',
                      '&:hover': { textDecoration: 'underline' }
                    }}
                  >
                    {item.itemUrl}
                  </Link>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    ไม่พบลิงก์สินค้า
                  </Typography>
                )}
              </Box>

              <Box mb={3}>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  label="บันทึกภายใน (ลูกค้าไม่เห็น)"
                  placeholder="เช่น เหตุผลที่ไม่ผ่าน"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  disabled={isSubmitting}
                />
              </Box>

              {confirmingFail && <FailureWarning outcome={failureOutcome} />}

              <Divider sx={{ my: 2 }} />

              <Box display="flex" justifyContent="flex-end" gap={2} flexWrap="wrap">
                {confirmingFail ? (
                  <>
                    <Button
                      variant="contained"
                      color="error"
                      onClick={handleFailed}
                      disabled={isSubmitting}
                      sx={{ minWidth: '140px' }}
                    >
                      {isSubmitting ? <CircularProgress size={24} /> : 'ยืนยัน ไม่ผ่าน'}
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={() => setConfirmingFail(false)}
                      disabled={isSubmitting}
                      sx={{ minWidth: '100px' }}
                    >
                      ย้อนกลับ
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      variant="contained"
                      color="success"
                      onClick={handlePassed}
                      disabled={isSubmitting}
                      sx={{ minWidth: '100px' }}
                    >
                      {isSubmitting ? <CircularProgress size={24} /> : 'ผ่าน'}
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={askWhatFailureWouldDo}
                      disabled={isSubmitting}
                      sx={{ minWidth: '100px' }}
                    >
                      ไม่ผ่าน
                    </Button>
                    {/* The expected answer most days, so it sits with the others
                        rather than looking like giving up. */}
                    <Button
                      variant="outlined"
                      onClick={handleDefer}
                      disabled={isSubmitting}
                      sx={{ minWidth: '120px' }}
                    >
                      {isSubmitting ? <CircularProgress size={24} /> : 'ยังไม่เสร็จ'}
                    </Button>
                    <Button
                      variant="outlined"
                      color="inherit"
                      onClick={handleClose}
                      disabled={isSubmitting}
                      sx={{ minWidth: '100px' }}
                    >
                      ปิด
                    </Button>
                  </>
                )}
              </Box>
            </>
          )}
        </Box>
      </Paper>
    </Modal>
  );
};

export default AppraisalModal;
